/**
 * ProtectedRoute
 * - Requires authentication
 * - Requires profile completion before checking PWG access
 */
import React from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../firebase/AuthContext";
import AccessPending from "./AccessPending";
import AccessUnavailable from "./AccessUnavailable";
import CompleteProfileModal from "./CompleteProfileModal";
import { useEntitlements } from "../entitlements/EntitlementContext";

export const hasProtectedApplicationAccess = (entitlement, application) =>
  (application === "MSI" && entitlement.isAdmin === true)
  || (entitlement.user?.status === "ACTIVE" && entitlement.applications?.[application]?.enabled === true);

export const protectedApplicationState = ({ user, entitlement, application }) => {
  if (user?.emailVerified !== true) return "verification_required";
  if (entitlement?.user?.status !== "ACTIVE") return "inactive";
  if (!hasProtectedApplicationAccess(entitlement, application)) return "not_entitled";
  return "authorized";
};

const ProtectedRoute = ({ children, application = "PWG" }) => {
  const {
    user,
    loading,
    authInitialized,
    userDoc,
    userDocLoading,
    firestoreBlocked,
    updateProfile,
  } = useAuth();
  const entitlement = useEntitlements();

  if (!authInitialized || loading) {
    return <div>Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (firestoreBlocked) {
    return (
      <AccessUnavailable reason="Google sign-in succeeded, but your account profile could not be loaded. Please try again or contact support." />
    );
  }

  if (userDocLoading || !userDoc) {
    return <div>Loading...</div>;
  }

  if (user.emailVerified !== true) {
    return (
      <AccessPending
        title="Email verification required"
        reason="Verify your email address, then sign in again to continue."
      />
    );
  }

  // Onboarding must happen before authorization checks.
  if (!userDoc.profileCompleted) {
    return (
      <CompleteProfileModal
        user={user}
        userDoc={userDoc}
        onSave={updateProfile}
      />
    );
  }

  const hasEstablishedAccess = hasProtectedApplicationAccess(entitlement, application);

  if (entitlement.loading && !hasEstablishedAccess) {
    return <div>Loading application access...</div>;
  }

  if (entitlement.accessState === "unavailable") {
    return <AccessUnavailable />;
  }

  if (entitlement.accessState === "checking" && !hasEstablishedAccess) {
    return <div>Loading application access...</div>;
  }

  if (protectedApplicationState({ user, entitlement, application }) !== "authorized") {
    return <AccessPending />;
  }

  return children;
};

export default ProtectedRoute;
